import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';


import '@carbon/web-components/es/components/tile/tile.js';
import '@carbon/web-components/es/components/progress-indicator/index.js';
import '@carbon/web-components/es/components/code-snippet/code-snippet.js';

import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import '../flow/pdq-json';
import '../flow/pdq-view';
import '../flow/pdq-perspective-viewer';
import './PDQCodeHtml';
import  '../sqlite/pdq-sqlite-console';
import  '../sqlite/pdq-sqlite-statement';
import  '../sqlite/pdq-sqlite-query';
import CodeIcon from '@carbon/web-components/es/icons/code/16';


@customElement('pdq-tutorial-sqlite')
export class PDQTutorialSqlite extends LitElement {
  render() {
    const codeStatements = `
<pdq-sqlite-local-database database="tutorial.db" ></pdq-sqlite-local-database>
<pdq-sqlite-statement database="tutorial.db" >
<script type="application/sql" slot="statement">
-- Create tables if they don't already exist
CREATE TABLE IF NOT EXISTS Users (
    user_id INTEGER PRIMARY KEY AUTOINCREMENT,
    name TEXT NOT NULL,
    email TEXT NOT NULL UNIQUE
);

CREATE TABLE IF NOT EXISTS Orders (
    order_id INTEGER PRIMARY KEY AUTOINCREMENT,
    user_id INTEGER NOT NULL,
    product TEXT NOT NULL,
    amount INTEGER NOT NULL,
    FOREIGN KEY(user_id) REFERENCES Users(id)
);

-- Insert sample data into Users if it doesn't already exist
INSERT OR IGNORE INTO Users (user_id, name, email) VALUES 
    (1, 'Alice', 'alice@example.com'),
    (2, 'Bob', 'bob@example.com'),
    (3, 'Charlie', 'charlie@example.com');

-- Insert sample data into Orders if it doesn't already exist
INSERT OR IGNORE INTO Orders (order_id, user_id, product, amount) VALUES 
    (1, 1, 'Book', 3),
    (2, 2, 'Pen', 10),
    (3, 1, 'Notebook', 2),
    (4, 3, 'Pencil', 5);
</script>
</pdq-sqlite-statement>
`

    const codeQuery = ` 
<pdq-sqlite-query database="tutorial.db" datasource="orders-ds" table="orders" index="order_id">
<script type="application/sql" slot="query">
SELECT * FROM Orders LEFT JOIN Users ON Orders.user_id = Users.user_id;
</script>
</pdq-sqlite-query>
`

    const codeDump =
      `<pdq-dump id="dump-people" source="orders-ds" show="1"></pdq-dump>`

    const codePerspective =
      `<pdq-perspective-viewer height="220px" table="orders"></pdq-perspective-viewer>`;

    const codeSqliteConsole = ` 
<pdq-sqlite-console database="tutorial.db" style="height:500px; display: block">
<script type="application/sql" slot="query">
SELECT * FROM sqlite_master;
</script>
</pdq-sqlite-console>
`;

    return html`
      <cds-tile>
        <h1>SQLite</h1>
        SQLite is a lightweight, self-contained, serverless, zero-configuration SQL database engine.
        It is widely used in embedded systems, mobile apps, and local data storage due to its simplicity and minimal resource requirements.

        PDQ includes an optional worker that is inited on first use, starting a web worker that uses
        <a href="https://sqlite.org/wasm/doc/trunk/index.md" target="_blank">@sqlite/sqlite-wasm</a> to run in the browser.

        The <cds-tag type="green">${CodeIcon({ slot: 'icon' })}pdq-sqlite-statement</cds-tag> component is used to execute SQL statements without response against a SQLite database.

        e.g.
        <pdq-code-html code="${codeStatements}"></pdq-code-html>
        ${unsafeHTML(codeStatements)}

        The <cds-tag type="green">${CodeIcon({ slot: 'icon' })}pdq-sqlite-query</cds-tag> component is used to execute SQL queries against a SQLite database,
        and saves the results to the global data model, and optionally directly to a <cds-tag type="green">pdq-table</cds-tag>

        <pdq-code-html code="${codeQuery}"></pdq-code-html>
        ${unsafeHTML(codeQuery)}
        
        The data can be subscribed to via the datasource name...
        <pdq-code-html code="${codeDump}"></pdq-code-html>
        ${unsafeHTML(codeDump)}

        ... or bound to a perspective viewer via the table
        <pdq-code-html code="${codePerspective}"></pdq-code-html>
        ${unsafeHTML(codePerspective)}
        
        The <cds-tag type="green">${CodeIcon({ slot: 'icon' })}pdq-sqlite-console</cds-tag> component 
        can be used to execute adhoc SQL queries against a SQLite database
        ${unsafeHTML(escapeHTML(codeSqliteConsole))}
        ${unsafeHTML(codeSqliteConsole)}

      </cds-tile>
    `;
  }
}

function escapeHTML(htmlString: string) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  } as any;
  return htmlString.replace(/[&<>"']/g, (match) => map[match]);
}